import { ReactNode } from 'react';
import { useTranslation, Trans } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { TextLink, Text, useModalControl, Tray, ModalControlModalProps, Button, Table } from '@frontend/design-system';
import { PhoneAnalyticsEntity } from '../components/call-group-queue-charts/types';
import { useRoutingAnalyticsData } from '../components/routing-analytics-context';

const getLocationNames = (accessibleLocationsNames: Record<string, string>, locationIds?: string[]) => {
  return locationIds?.map((id) => accessibleLocationsNames[id]) ?? [];
};

export const EmptyStateAnalyticsText = ({
  entityType,
  minAllowedDate,
  isDateValid,
}: {
  entityType: string;
  minAllowedDate: string;
  isDateValid: boolean;
}): ReactNode => {
  const { accessibleLocationsNames, filters } = useRoutingAnalyticsData();
  const { t } = useTranslation('analytics-phone');
  const { openModal, modalProps } = useModalControl();

  const locationNames = getLocationNames(accessibleLocationsNames, filters.location_id);
  const isMulti = locationNames.length > 1;
  const callGroupText = entityType === PhoneAnalyticsEntity.Group ? t('Call Groups') : '';
  const callQueueText = entityType === PhoneAnalyticsEntity.Queue ? t('Call Queues') : '';
  const phoneTreesText = entityType === PhoneAnalyticsEntity.PhoneTrees ? t('Phone Trees') : '';
  const entityTypeText = callGroupText || callQueueText || phoneTreesText;

  return (
    <>
      <Text textAlign='center' css={{ color: theme.colorsV2.neutral50(), maxWidth: '512px' }}>
        {isMulti ? (
          <MultLocationsText
            entityTypeText={entityTypeText}
            locationNames={locationNames}
            isDateValid={isDateValid}
            minAllowedDate={minAllowedDate}
            onOpenModal={openModal}
          />
        ) : (
          <SingleLocationText
            entityTypeText={entityTypeText}
            isDateValid={isDateValid}
            locationName={locationNames[0]}
            minAllowedDate={minAllowedDate}
          />
        )}
      </Text>
      <LocationModalTray locationNames={locationNames} modalProps={modalProps} />
    </>
  );
};

const MultLocationsText = ({
  entityTypeText,
  locationNames,
  isDateValid,
  minAllowedDate,
  onOpenModal,
}: {
  entityTypeText: string;
  locationNames: string[];
  isDateValid: boolean;
  minAllowedDate: string;
  onOpenModal: () => void;
}) => {
  const visible = locationNames.slice(0, 3);
  const remaining = Math.max(0, locationNames.length - visible.length);

  if (locationNames.length === 2) {
    return isDateValid ? (
      <Trans>
        {visible[0]} and {visible[1]} do not have any {entityTypeText} in their call routing. When you add{' '}
        {entityTypeText} to your call routing, their analytics will display here.
      </Trans>
    ) : (
      <Trans>
        {visible[0]} and {visible[1]} do not have any {entityTypeText} in their call routing for this date range. When
        you select a date range after {minAllowedDate}, these analytics will display here.
      </Trans>
    );
  }

  if (locationNames.length === 3) {
    return isDateValid ? (
      <Trans>
        {visible[0]}, {visible[1]} and {visible[2]} do not have any {entityTypeText} in their call routing. When you add{' '}
        {entityTypeText} to your call routing, their analytics will display here.
      </Trans>
    ) : (
      <Trans>
        {visible[0]}, {visible[1]} and {visible[2]} do not have any {entityTypeText} in their call routing for this date
        range. When you select a date range after {minAllowedDate}, these analytics will display here.
      </Trans>
    );
  }

  // More than 3 locations
  return isDateValid ? (
    <Trans>
      {visible[0]}, {visible[1]}, {visible[2]} and{' '}
      <TextLink as='span' onClick={onOpenModal}>
        +{`${remaining}`} more locations
      </TextLink>{' '}
      do not have any {entityTypeText} in their call routing. When you add {entityTypeText} to your call routing, their
      analytics will display here.
    </Trans>
  ) : (
    <Trans>
      {visible[0]}, {visible[1]}, {visible[2]} and{' '}
      <TextLink as='span' onClick={onOpenModal}>
        +{`${remaining}`} more locations
      </TextLink>{' '}
      do not have any {entityTypeText} in their call routing for this date range. When you select a date range after{' '}
      {minAllowedDate}, these analytics will display here.
    </Trans>
  );
};

const SingleLocationText = ({
  entityTypeText,
  isDateValid,
  locationName,
  minAllowedDate,
}: {
  entityTypeText: string;
  isDateValid: boolean;
  locationName: string;
  minAllowedDate: string;
}) => {
  const { t } = useTranslation('analytics-phone');
  const text = isDateValid
    ? t(
        '{{locationName}} does not have any {{entityTypeText}} in their call routing. When you add {{entityTypeText}} to your call routing, these analytics will display here.',
        { entityTypeText, locationName }
      )
    : t(
        '{{locationName}} does not have any {{entityTypeText}} in their call routing for this date range. When you select a date range after {{minAllowedDate}}, analytics will display here.',
        { entityTypeText, minAllowedDate, locationName }
      );
  return <Text>{text}</Text>;
};

const LocationModalTray = ({
  locationNames,
  modalProps,
}: {
  locationNames: string[];
  modalProps: ModalControlModalProps;
}) => {
  const { t } = useTranslation('analytics-phone');

  return (
    <Tray {...modalProps} css={{}}>
      <Tray.Header Buttons={<Button variant='secondary' iconName='x' onClick={modalProps.onClose} />}>
        {t('Locations')}
      </Tray.Header>
      <Tray.Body>
        <Table
          data={locationNames.map((name) => ({ locationName: name }))}
          allowMobileView
          colConfig={[
            {
              Header: '',
              accessor: (row) => row.locationName,
              id: 'locationName',
            },
          ]}
        />
      </Tray.Body>
    </Tray>
  );
};
